import React, { useState, useEffect } from 'react';
import { api } from '../../lib/api';
import { format, parseISO, isSameMonth, isSameYear, isValid } from 'date-fns';
import esLocale from 'date-fns/locale/es';
import { registerLocale } from 'react-datepicker';

import { motion } from 'framer-motion';
import { Download, Calendar, Eye, EyeOff, Users, Anchor} from 'lucide-react';
import * as XLSX from 'xlsx';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import Button from '../ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '../ui/table';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../ui/select';

registerLocale('es', esLocale);

const HistoricoMovimientos = () => {
  const [historicoMovimientos, setHistoricoMovimientos] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedView, setSelectedView] = useState('mes');
  const [displayedMovimientos, setDisplayedMovimientos] = useState([]);
  const [showMovimientos, setShowMovimientos] = useState({});
  const [selectedExportDates, setSelectedExportDates] = useState([]);

  const parseToValidDate = (value) => {
    if (!value) return null;
    if (value instanceof Date) {
      return isValid(value) ? value : null;
    }

    try {
      const parsed = parseISO(value);
      return isValid(parsed) ? parsed : null;
    } catch (error) {
      return null;
    }
  };

  const calculateColumnWidths = (
    worksheet,
    totalColumns,
    { minWidth = 12, maxWidth = 80 } = {}
  ) => {
    if (!worksheet || !worksheet['!ref']) return;

    const range = XLSX.utils.decode_range(worksheet['!ref']);
    const startColumn = range.s.c;
    const computedWidths = [];

    for (let columnIndex = 0; columnIndex < totalColumns; columnIndex++) {
      const worksheetColumn = startColumn + columnIndex;
      let longestContent = 0;

      for (let rowIndex = range.s.r; rowIndex <= range.e.r; rowIndex++) {
        const cellAddress = XLSX.utils.encode_cell({
          r: rowIndex,
          c: worksheetColumn
        });
        const cell = worksheet[cellAddress];
        if (!cell) continue;

        const rawValue = cell.w ?? cell.v;
        if (rawValue === undefined || rawValue === null) continue;

        const cellText = rawValue.toString();
        const longestLine = cellText
          .split(/\r?\n/)
          .reduce((max, line) => Math.max(max, line.length), 0);

        longestContent = Math.max(longestContent, longestLine);
      }

      const adjustedWidth = Math.min(
        maxWidth,
        Math.max(minWidth, longestContent + 4)
      );

      computedWidths.push({ wch: adjustedWidth });
    }

    worksheet['!cols'] = computedWidths;
  };

  // Trae todos los movimientos desde el backend
 useEffect(() => {
  const fetchData = async () => {
    try {
      const response = await api.get('/historico-movimientos');
      console.log('Datos del histórico:', response.data);
      setHistoricoMovimientos(response.data);

    } catch (error) {
      console.error('Error cargando el histórico:', error);
    }
  };

  fetchData();
}, []);


  // Cada vez que cambia la selección de fecha, vista o el array de movimientos,
  // recalculamos los mov. a mostrar (excluyendo los del día actual)
  useEffect(() => {
    filterMovimientos();
  }, [selectedDate, selectedView, historicoMovimientos]);

  const filterMovimientos = () => {
  const hoy = new Date();

  const mesSeleccionado = selectedDate.getMonth();
  const añoSeleccionado = selectedDate.getFullYear();

  const filtered = historicoMovimientos.filter((movimiento) => {
    if (!movimiento.fechaHoraSalida) return false;  // <- Cambiado

    const fecha = new Date(movimiento.fechaHoraSalida); // <- Cambiado
    if (isNaN(fecha)) return false;

    const mesMovimiento = fecha.getMonth();
    const añoMovimiento = fecha.getFullYear();

    const esHoy =
      fecha.getFullYear() === hoy.getFullYear() &&
      fecha.getMonth() === hoy.getMonth() &&
      fecha.getDate() === hoy.getDate();

    if (selectedView === 'mes') {
      return (
        mesMovimiento === mesSeleccionado &&
        añoMovimiento === añoSeleccionado &&
        !esHoy
      );
    }

    if (selectedView === 'año') {
      return (
        añoMovimiento === añoSeleccionado &&
        !esHoy
      );
    }

    return false;
  });

  setDisplayedMovimientos(filtered);
};

const groupByDate = (movimientos) => {
  return movimientos.reduce((acc, movimiento) => {
    if (!movimiento.fechaHoraSalida) return acc; // <- Cambiado

    const fecha = new Date(movimiento.fechaHoraSalida); // <- Cambiado
    if (isNaN(fecha)) return acc;

    const dateKey = format(fecha, 'yyyy-MM-dd');

    if (!acc[dateKey]) acc[dateKey] = [];
    acc[dateKey].push(movimiento);

    return acc;
  }, {});
};


 
  const groupedMovimientos = groupByDate(displayedMovimientos);
  const dates = Object.keys(groupedMovimientos).sort((a, b) => new Date(b) - new Date(a));

  const resumenDelMes = {
  totalClientes: new Set(displayedMovimientos.map(m => m.cliente_id)).size,
  totalEmbarcaciones: new Set(displayedMovimientos.map(m => m.embarcacion_numeroMatricula)).size,
  totalMovimientos: displayedMovimientos.length
};

  // Función para crear el encabezado del Excel diario
  const createDailyHeader = (reportDateLabel, resumen, columnCount) => {
    const totalColumns = Math.max(columnCount, 1);

    const createRow = (label, value) => {
      const row = Array(totalColumns).fill('');
      row[0] = label;
      if (value !== undefined) {
        row[1] = value;
      }
      return row;
    };

    return [
      createRow('HISTÓRICO DE MOVIMIENTOS - El Biguá'),
      createRow('Reporte diario de embarcaciones y clientes'),
      createRow('Fecha del reporte:', reportDateLabel),
      createRow('Generado el:', format(new Date(), 'dd/MM/yyyy HH:mm')),
      createRow('Total de movimientos registrados:', resumen.totalMovimientos),
      createRow('Embarcaciones únicas en el día:', resumen.totalEmbarcaciones),
      createRow('Clientes únicos en el día:', resumen.totalClientes),
      Array(totalColumns).fill('')
    ];
  };

  // Función para aplicar estilos avanzados a los Excel diarios
  const applyDailyExcelStyles = (worksheet, headerRowCount = 6, columnHeaders = []) => {
    const range = XLSX.utils.decode_range(worksheet['!ref']);
    const columnCount = columnHeaders.length || (range.e.c - range.s.c + 1);

    const deepClone = (obj) => {
      if (!obj) return obj;
      return JSON.parse(JSON.stringify(obj));
    };

    const mergeStyles = (baseStyle, overrideStyle = {}) => {
      const merged = deepClone(baseStyle) || {};

      if (overrideStyle.font) {
        merged.font = { ...(merged.font || {}), ...overrideStyle.font };
      }

      if (overrideStyle.alignment) {
        merged.alignment = { ...(merged.alignment || {}), ...overrideStyle.alignment };
      }

      if (overrideStyle.fill) {
        merged.fill = { ...(merged.fill || {}), ...overrideStyle.fill };
      }

      if (overrideStyle.border) {
        merged.border = { ...(merged.border || {}), ...overrideStyle.border };
      }

      if (overrideStyle.numFmt) {
        merged.numFmt = overrideStyle.numFmt;
        merged.z = overrideStyle.numFmt;
      }

      return merged;
    };

    const applyStyleToCell = (rowIndex, columnIndex, style) => {
      const cellAddress = XLSX.utils.encode_cell({ r: rowIndex, c: columnIndex });
      if (worksheet[cellAddress]) {
        worksheet[cellAddress].s = deepClone(style);
      }
    };

    // Estilos para el título principal
    const titleStyle = {
      fill: { fgColor: { rgb: "1E3A8A" } },
      font: { color: { rgb: "FFFFFF" }, bold: true, sz: 16 },
      alignment: { horizontal: "center", vertical: "center" },
      border: {
        top: { style: "thick", color: { rgb: "000000" } },
        bottom: { style: "thick", color: { rgb: "000000" } },
        left: { style: "thick", color: { rgb: "000000" } },
        right: { style: "thick", color: { rgb: "000000" } }
      }
    };

    const subtitleStyle = {
      fill: { fgColor: { rgb: "1D4ED8" } },
      font: { color: { rgb: "FFFFFF" }, bold: true, sz: 12 },
      alignment: { horizontal: "center", vertical: "center" }
    };

    // Estilos para información del encabezado
    const infoLabelStyle = {
      fill: { fgColor: { rgb: "059669" } },
      font: { color: { rgb: "FFFFFF" }, bold: true, sz: 11 },
      alignment: { horizontal: "right", vertical: "center" },
      border: {
        top: { style: "thin", color: { rgb: "000000" } },
        bottom: { style: "thin", color: { rgb: "000000" } },
        left: { style: "thin", color: { rgb: "000000" } },
        right: { style: "thin", color: { rgb: "000000" } }
      }
    };

    const infoValueStyle = {
      fill: { fgColor: { rgb: "FEF3C7" } },
      font: { color: { rgb: "92400E" }, bold: true, sz: 11 },
      alignment: { horizontal: "left", vertical: "center" },
      border: {
        top: { style: "thin", color: { rgb: "000000" } },
        bottom: { style: "thin", color: { rgb: "000000" } },
        left: { style: "thin", color: { rgb: "000000" } },
        right: { style: "thin", color: { rgb: "000000" } }
      }
    };

    const metricLabelStyle = {
      fill: { fgColor: { rgb: "0F172A" } },
      font: { color: { rgb: "FFFFFF" }, bold: true, sz: 11 },
      alignment: { horizontal: "right", vertical: "center" },
      border: {
        top: { style: "thin", color: { rgb: "000000" } },
        bottom: { style: "thin", color: { rgb: "000000" } },
        left: { style: "thin", color: { rgb: "000000" } },
        right: { style: "thin", color: { rgb: "000000" } }
      }
    };

    const metricValueStyle = {
      fill: { fgColor: { rgb: "F97316" } },
      font: { color: { rgb: "FFFFFF" }, bold: true, sz: 12 },
      alignment: { horizontal: "left", vertical: "center" },
      border: {
        top: { style: "thin", color: { rgb: "000000" } },
        bottom: { style: "thin", color: { rgb: "000000" } },
        left: { style: "thin", color: { rgb: "000000" } },
        right: { style: "thin", color: { rgb: "000000" } }
      }
    };

    // Estilos para encabezados de tabla
    const tableHeaderStyle = {
      fill: { fgColor: { rgb: "1E40AF" } },
      font: { color: { rgb: "FFFFFF" }, bold: true, sz: 10 },
      alignment: { horizontal: "center", vertical: "center", wrapText: true },
      border: {
        top: { style: "medium", color: { rgb: "000000" } },
        bottom: { style: "medium", color: { rgb: "000000" } },
        left: { style: "thin", color: { rgb: "000000" } },
        right: { style: "thin", color: { rgb: "000000" } }
      }
    };

    // Estilos para celdas de datos (filas pares)
    const evenRowStyle = {
      fill: { fgColor: { rgb: "F1F5F9" } },
      font: { color: { rgb: "1E293B" }, sz: 9 },
      alignment: { horizontal: "left", vertical: "center" },
      border: {
        top: { style: "thin", color: { rgb: "CBD5E1" } },
        bottom: { style: "thin", color: { rgb: "CBD5E1" } },
        left: { style: "thin", color: { rgb: "CBD5E1" } },
        right: { style: "thin", color: { rgb: "CBD5E1" } }
      }
    };

    // Estilos para celdas de datos (filas impares)
    const oddRowStyle = {
      fill: { fgColor: { rgb: "FFFFFF" } },
      font: { color: { rgb: "1E293B" }, sz: 9 },
      alignment: { horizontal: "left", vertical: "center" },
      border: {
        top: { style: "thin", color: { rgb: "CBD5E1" } },
        bottom: { style: "thin", color: { rgb: "CBD5E1" } },
        left: { style: "thin", color: { rgb: "CBD5E1" } },
        right: { style: "thin", color: { rgb: "CBD5E1" } }
      }
    };

    // Aplicar estilos principales del encabezado
    applyStyleToCell(0, 0, titleStyle);
    applyStyleToCell(1, 0, subtitleStyle);

    const infoRows = [2, 3];
    infoRows.forEach((row) => {
      applyStyleToCell(row, 0, infoLabelStyle);
      applyStyleToCell(row, 1, infoValueStyle);
    });

    const metricRows = [4, 5, 6];
    metricRows.forEach((row) => {
      applyStyleToCell(row, 0, metricLabelStyle);
      applyStyleToCell(row, 1, metricValueStyle);
    });

    // Aplicar estilos a encabezados de tabla (fila después del header)
    const headerRow = headerRowCount;
    for (let col = range.s.c; col <= range.e.c; col++) {
      const cellAddress = XLSX.utils.encode_cell({ r: headerRow, c: col });
      if (worksheet[cellAddress]) {
        worksheet[cellAddress].s = tableHeaderStyle;
      }
    }

    const overridesByHeader = {
      ID: { alignment: { horizontal: "center" }, font: { bold: true } },
      'Cliente ID': { alignment: { horizontal: "center" } },
      Matrícula: { alignment: { horizontal: "center" } },
      Seguro: { alignment: { horizontal: "center" } },
      'Vencimiento Seguro': { alignment: { horizontal: "center" } },
      'Tipo de Embarcación': { alignment: { horizontal: "left" } },
      'Nombre Fantasía': { alignment: { horizontal: "left" } },
      HP: { alignment: { horizontal: "center" } },
      Capacidad: { alignment: { horizontal: "center" } },
      'Color Casco': { alignment: { horizontal: "center" } },
      Cliente: { alignment: { horizontal: "left" } },
      'DNI Cliente': { alignment: { horizontal: "center" } },
      'Teléfono 1': { alignment: { horizontal: "center" } },
      'Teléfono 2': { alignment: { horizontal: "center" } },
      'Vencimiento Licencia': { alignment: { horizontal: "center" } },
      'Marca Vehículo': { alignment: { horizontal: "center" } },
      'Patente Vehículo': { alignment: { horizontal: "center" } },
      'Color Vehículo': { alignment: { horizontal: "center" } },
      'Fecha Ingreso': { alignment: { horizontal: "center" } },
      'Fecha Salida': { alignment: { horizontal: "center" } },
      Estado: { alignment: { horizontal: "center" }, font: { bold: true } },
      'Tipo de Movimiento': { alignment: { horizontal: "center" }, font: { bold: true } },
      Observaciones: { alignment: { horizontal: "left", vertical: "top", wrapText: true } }
    };

    const columnStyleOverrides = columnHeaders.reduce((acc, header, index) => {
      if (overridesByHeader[header]) {
        acc[index] = overridesByHeader[header];
      }
      return acc;
    }, {});

    // Aplicar estilos a filas de datos
    for (let row = headerRow + 1; row <= range.e.r; row++) {
      const isEven = (row - headerRow) % 2 === 0;
      for (let col = range.s.c; col <= range.e.c; col++) {
        const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
        if (worksheet[cellAddress]) {
          const baseStyle = isEven ? evenRowStyle : oddRowStyle;
          const override = columnStyleOverrides[col - range.s.c] || {};
          worksheet[cellAddress].s = mergeStyles(baseStyle, override);
        }
      }
    }

    // Configurar merge cells para el título y la información
    const merges = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: columnCount - 1 } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: columnCount - 1 } },
      { s: { r: 2, c: 1 }, e: { r: 2, c: Math.min(4, columnCount - 1) } },
      { s: { r: 3, c: 1 }, e: { r: 3, c: Math.min(4, columnCount - 1) } },
      { s: { r: 4, c: 1 }, e: { r: 4, c: Math.min(4, columnCount - 1) } },
      { s: { r: 5, c: 1 }, e: { r: 5, c: Math.min(4, columnCount - 1) } },
      { s: { r: 6, c: 1 }, e: { r: 6, c: Math.min(4, columnCount - 1) } }
    ];

    worksheet['!merges'] = [...(worksheet['!merges'] || []), ...merges];

    // Ajustar automáticamente los anchos de columnas según el contenido
    calculateColumnWidths(worksheet, columnCount, { minWidth: 14, maxWidth: 90 });

    // Configurar altura de filas y visibilidad del encabezado
    if (!worksheet['!rows']) worksheet['!rows'] = [];
    worksheet['!rows'][0] = { hpt: 28 };
    worksheet['!rows'][1] = { hpt: 22 };
    worksheet['!rows'][headerRow] = { hpt: 38 };

    // No se aplican autofiltros para mantener el diseño limpio y enfocado en el formato
    worksheet['!freeze'] = { xSplit: 0, ySplit: headerRowCount + 1 };
    worksheet['!pane'] = {
      state: 'frozen',
      ySplit: headerRowCount + 1,
      topLeftCell: XLSX.utils.encode_cell({ r: headerRowCount + 1, c: 0 }),
      activePane: 'bottomLeft'
    };

    return worksheet;
  };

  const exportMovimientosToExcel = ({ movimientos, reportDateLabel, fileName }) => {
    const totalMovimientos = movimientos.length;

    if (totalMovimientos === 0) {
      console.warn('No hay movimientos para exportar');
      return;
    }

    const resumenDia = {
      totalMovimientos,
      totalEmbarcaciones: new Set(
        movimientos
          .map((movimiento) => movimiento.embarcacion_numeroMatricula)
          .filter((value) => value !== null && value !== undefined && value !== '')
      ).size,
      totalClientes: new Set(
        movimientos
          .map((movimiento) => movimiento.cliente_id)
          .filter((value) => value !== null && value !== undefined && value !== '')
      ).size
    };

    const formatDateValue = (value, dateFormat, allowEmpty = false) => {
      const parsed = parseToValidDate(value);
      if (!parsed) {
        return allowEmpty ? '' : 'N/A';
      }

      return format(parsed, dateFormat);
    };

    // Datos de los movimientos
    const dataToExport = movimientos.map((movimiento) => {
      const nombreCompleto = `${movimiento.cliente_nombre ?? ''} ${
        movimiento.cliente_apellido ?? ''
      }`.trim();

      return {
        ID: movimiento.id ?? 'N/A',
        'Cliente ID': movimiento.cliente_id ?? 'N/A',
        Matrícula: movimiento.embarcacion_numeroMatricula ?? 'N/A',
        Seguro: movimiento.embarcacion_seguro ?? 'N/A',
        'Vencimiento Seguro': formatDateValue(
          movimiento.embarcacion_vencimientoSeguro,
          'dd/MM/yyyy'
        ),
        'Vencimiento Matrícula': formatDateValue(
          movimiento.embarcacion_vencimientoMatricula,
          'dd/MM/yyyy',
          true
        ),
        'Tipo de Embarcación': movimiento.embarcacion_tipoEmbarcacion ?? 'N/A',
        'Nombre Fantasía': movimiento.embarcacion_nombreFantasia ?? 'N/A',
        HP: movimiento.embarcacion_hp ?? 'N/A',
        Capacidad: movimiento.embarcacion_capacidad ?? 'N/A',
        'Color Casco': movimiento.embarcacion_colorCasco ?? 'N/A',
        Cliente: nombreCompleto || 'N/A',
        'DNI Cliente': movimiento.cliente_dni ?? 'N/A',
        'Teléfono 1': movimiento.cliente_telefono1 ?? 'N/A',
        'Teléfono 2': movimiento.cliente_telefono2 ?? 'N/A',
        'Vencimiento Licencia': formatDateValue(
          movimiento.cliente_vencimientoLicencia,
          'dd/MM/yyyy'
        ),
        'Marca Vehículo': movimiento.vehiculo_marca ?? 'N/A',
        'Patente Vehículo': movimiento.vehiculo_patente ?? 'N/A',
        'Color Vehículo': movimiento.vehiculo_color ?? 'N/A',
        'Fecha Ingreso': formatDateValue(
          movimiento.fechaHoraIngreso,
          'dd/MM/yyyy HH:mm'
        ),
        'Fecha Salida': formatDateValue(
          movimiento.fechaHoraSalida,
          'dd/MM/yyyy HH:mm'
        ),
        'Tipo de Movimiento': movimiento.tipoMovimiento ?? 'N/A',
        Estado: movimiento.estado ?? 'N/A',
        Observaciones: movimiento.observaciones ?? 'N/A'
      };
    });

    const columnHeaders = Object.keys(dataToExport[0] || {});

    // Crear encabezado personalizado
    const headerData = createDailyHeader(reportDateLabel, resumenDia, columnHeaders.length);

    // Combinar encabezado con datos
    const fullData = [
      ...headerData,
      columnHeaders,
      ...dataToExport.map((row) => columnHeaders.map((key) => row[key]))
    ];

    // Crear worksheet desde array
    let worksheet = XLSX.utils.aoa_to_sheet(fullData);

    // Aplicar estilos avanzados
    worksheet = applyDailyExcelStyles(worksheet, headerData.length, columnHeaders);

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Movimientos Diarios');
    
    // Configurar propiedades del libro
    workbook.Props = {
      Title: `Movimientos del ${reportDateLabel}`,
      Subject: "Histórico de Movimientos Diarios",
      Author: "Driza - Gestión de Campings",
      Manager: "Departamento de Operaciones",
      Company: "Marina Club Náutico",
      CreatedDate: new Date()
    };

    XLSX.writeFile(workbook, fileName);
    
    // Mostrar notificación de éxito (opcional)
    console.log(`Excel generado exitosamente: ${fileName}`);
  };

  // Exporta a Excel todos los campos de los movimientos de una fecha determinada
  const handleDownload = (date) => {
    const movimientosDelDia = groupedMovimientos[date] || [];
    const fechaArchivo = format(parseISO(date), 'yyyy-MM-dd');

    exportMovimientosToExcel({
      movimientos: movimientosDelDia,
      reportDateLabel: format(parseISO(date), 'dd/MM/yyyy'),
      fileName: `movimientos_${fechaArchivo}.xlsx`
    });
  };

  const toggleDateForExport = (date) => {
    setSelectedExportDates((prev) =>
      prev.includes(date) ? prev.filter((item) => item !== date) : [...prev, date]
    );
  };

  const handleDownloadSelectedDates = () => {
    if (selectedExportDates.length === 0) {
      return;
    }

    const sortedDates = [...selectedExportDates].sort((a, b) => new Date(a) - new Date(b));
    const movimientosSeleccionados = sortedDates.flatMap((date) => groupedMovimientos[date] || []);

    const labelFechas =
      sortedDates.length === 1
        ? format(parseISO(sortedDates[0]), 'dd/MM/yyyy')
        : `${format(parseISO(sortedDates[0]), 'dd/MM/yyyy')} al ${format(
            parseISO(sortedDates[sortedDates.length - 1]),
            'dd/MM/yyyy'
          )}`;

    const fechaInicioArchivo = format(parseISO(sortedDates[0]), 'yyyy-MM-dd');
    const fechaFinArchivo = format(parseISO(sortedDates[sortedDates.length - 1]), 'yyyy-MM-dd');

    exportMovimientosToExcel({
      movimientos: movimientosSeleccionados,
      reportDateLabel: labelFechas,
      fileName: `movimientos_fechas_${fechaInicioArchivo}_a_${fechaFinArchivo}.xlsx`
    });
  };

  const handleSelectedDatesFromControl = (event) => {
    const selectedValues = Array.from(event.target.selectedOptions, (option) => option.value);
    setSelectedExportDates(selectedValues);
  };

  // Avanza/retrocede mes o año al hacer clic en las flechas
  const changeDate = (amount) => {
    if (selectedView === 'mes') {
      setSelectedDate((date) => new Date(date.setMonth(date.getMonth() + amount)));
    } else if (selectedView === 'año') {
      setSelectedDate((date) => new Date(date.setFullYear(date.getFullYear() + amount)));
    }
  };

  // Alterna la visibilidad de la tabla para una fecha específica
  const toggleShowMovimientos = (date) => {
    setShowMovimientos((prev) => ({
      ...prev,
      [date]: !prev[date]
    }));
  };

  const descargarResumenMes = () => {
    const diasConMovimiento = new Set(
      displayedMovimientos
        .map((movimiento) => {
          const fecha = parseToValidDate(
            movimiento.fechaHoraSalida || movimiento.fechaHoraIngreso
          );
          return fecha ? format(fecha, 'yyyy-MM-dd') : null;
        })
        .filter(Boolean)
    ).size;

    const promedioMovimientos = diasConMovimiento
      ? Number((resumenDelMes.totalMovimientos / diasConMovimiento).toFixed(1))
      : 0;

    const resumenData = [
      ['HISTÓRICO DE MOVIMIENTOS - Resumen Mensual', '', ''],
      ['Reporte consolidado de embarcaciones y clientes', '', ''],
      ['Período:', format(selectedDate, 'MMMM yyyy', { locale: esLocale }), ''],
      ['Generado el:', format(new Date(), 'dd/MM/yyyy HH:mm'), ''],
      ['Días con movimientos registrados:', diasConMovimiento, ''],
      Array(3).fill(''),
      ['Indicador', 'Valor', 'Descripción'],
      [
        'Clientes únicos',
        resumenDelMes.totalClientes,
        'Cantidad de clientes diferentes con movimientos durante el período seleccionado'
      ],
      [
        'Embarcaciones únicas',
        resumenDelMes.totalEmbarcaciones,
        'Número de matrículas distintas que registraron actividad en el período'
      ],
      [
        'Movimientos registrados',
        resumenDelMes.totalMovimientos,
        'Total de ingresos y egresos registrados para el período seleccionado'
      ],
      [
        'Promedio diario de movimientos',
        promedioMovimientos,
        'Promedio calculado sobre los días con al menos un movimiento registrado'
      ]
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(resumenData);

    const titleStyle = {
      fill: { fgColor: { rgb: '1E3A8A' } },
      font: { color: { rgb: 'FFFFFF' }, bold: true, sz: 16 },
      alignment: { horizontal: 'center', vertical: 'center' }
    };

    const subtitleStyle = {
      fill: { fgColor: { rgb: '1D4ED8' } },
      font: { color: { rgb: 'FFFFFF' }, bold: true, sz: 12 },
      alignment: { horizontal: 'center', vertical: 'center' }
    };

    const infoLabelStyle = {
      fill: { fgColor: { rgb: '059669' } },
      font: { color: { rgb: 'FFFFFF' }, bold: true, sz: 11 },
      alignment: { horizontal: 'right', vertical: 'center' },
      border: {
        top: { style: 'thin', color: { rgb: '000000' } },
        bottom: { style: 'thin', color: { rgb: '000000' } },
        left: { style: 'thin', color: { rgb: '000000' } },
        right: { style: 'thin', color: { rgb: '000000' } }
      }
    };

    const infoValueStyle = {
      fill: { fgColor: { rgb: 'FEF3C7' } },
      font: { color: { rgb: '92400E' }, bold: true, sz: 11 },
      alignment: { horizontal: 'left', vertical: 'center' },
      border: {
        top: { style: 'thin', color: { rgb: '000000' } },
        bottom: { style: 'thin', color: { rgb: '000000' } },
        left: { style: 'thin', color: { rgb: '000000' } },
        right: { style: 'thin', color: { rgb: '000000' } }
      }
    };

    const headerStyle = {
      fill: { fgColor: { rgb: '0F172A' } },
      font: { color: { rgb: 'FFFFFF' }, bold: true, sz: 12 },
      alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
      border: {
        top: { style: 'medium', color: { rgb: '000000' } },
        bottom: { style: 'medium', color: { rgb: '000000' } },
        left: { style: 'medium', color: { rgb: '000000' } },
        right: { style: 'medium', color: { rgb: '000000' } }
      }
    };

    const indicatorStyle = {
      fill: { fgColor: { rgb: 'F9FAFB' } },
      font: { color: { rgb: '111827' }, bold: true, sz: 11 },
      alignment: { horizontal: 'left', vertical: 'center', wrapText: true },
      border: {
        top: { style: 'thin', color: { rgb: 'D1D5DB' } },
        bottom: { style: 'thin', color: { rgb: 'D1D5DB' } },
        left: { style: 'thin', color: { rgb: '9CA3AF' } },
        right: { style: 'thin', color: { rgb: '9CA3AF' } }
      }
    };

    const valueStyle = {
      fill: { fgColor: { rgb: 'FDE68A' } },
      font: { color: { rgb: '92400E' }, bold: true, sz: 12 },
      alignment: { horizontal: 'center', vertical: 'center' },
      border: {
        top: { style: 'thin', color: { rgb: 'D97706' } },
        bottom: { style: 'thin', color: { rgb: 'D97706' } },
        left: { style: 'thin', color: { rgb: 'D97706' } },
        right: { style: 'thin', color: { rgb: 'D97706' } }
      }
    };

    const descriptionStyle = {
      fill: { fgColor: { rgb: 'FFFFFF' } },
      font: { color: { rgb: '1F2937' }, sz: 10 },
      alignment: { horizontal: 'left', vertical: 'top', wrapText: true },
      border: {
        top: { style: 'thin', color: { rgb: 'D1D5DB' } },
        bottom: { style: 'thin', color: { rgb: 'D1D5DB' } },
        left: { style: 'thin', color: { rgb: '9CA3AF' } },
        right: { style: 'thin', color: { rgb: '9CA3AF' } }
      }
    };

    const setCellStyle = (cellRef, style) => {
      if (worksheet[cellRef]) {
        worksheet[cellRef].s = style;
      }
    };

    setCellStyle('A1', titleStyle);
    setCellStyle('A2', subtitleStyle);

    const infoRows = [3, 4, 5];
    infoRows.forEach((rowNumber) => {
      const labelCell = `A${rowNumber}`;
      const valueCell = `B${rowNumber}`;
      setCellStyle(labelCell, infoLabelStyle);
      setCellStyle(valueCell, infoValueStyle);
    });

    const headerRowIndex = resumenData.findIndex((row) => row[0] === 'Indicador');
    const headerRowNumber = headerRowIndex + 1;
    ['A', 'B', 'C'].forEach((column) => {
      setCellStyle(`${column}${headerRowNumber}`, headerStyle);
    });

    for (let i = headerRowIndex + 1; i < resumenData.length; i++) {
      const excelRow = i + 1;
      setCellStyle(`A${excelRow}`, indicatorStyle);
      setCellStyle(`B${excelRow}`, valueStyle);
      setCellStyle(`C${excelRow}`, descriptionStyle);
    }

    worksheet['!merges'] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: 2 } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: 2 } }
    ];

    calculateColumnWidths(worksheet, 3, { minWidth: 22, maxWidth: 75 });

    worksheet['!rows'] = worksheet['!rows'] || [];
    worksheet['!rows'][0] = { hpt: 28 };
    worksheet['!rows'][1] = { hpt: 22 };
    worksheet['!rows'][headerRowIndex] = { hpt: 30 };

    // No se agregan autofiltros para priorizar la presentación visual del resumen

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Resumen Mensual');

    workbook.Props = {
      Title: `Resumen Mensual - ${format(selectedDate, 'MMMM yyyy', { locale: esLocale })}`,
      Subject: 'Resumen de Movimientos Mensuales',
      Author: 'Driza - Gestion de Campings',
      CreatedDate: new Date()
    };

    const nombreArchivo = `resumen_movimientos_${format(selectedDate, 'yyyy-MM')}.xlsx`;
    XLSX.writeFile(workbook, nombreArchivo);
  };


  return (
    <div className="min-h-screen bg-gradient-to-br from-azul-oscuro to-azul-lago text-blanco-puro">
      <main>
        <motion.h1
          className="text-4xl font-bold mb-8 text-amarillo-dorado"
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          Histórico de Movimientos
        </motion.h1>

        
         <Card className="bg-verde-esmeralda bg-opacity-10 p-6 mb-8">
  <h2 className="text-2xl font-semibold mb-4 text-center text-verde-esmeralda">
    Resumen del Mes - {format(selectedDate, 'MMMM yyyy', { locale: esLocale })}
  </h2>
  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
    <div className="text-center bg-azul-oscuro bg-opacity-30 p-4 rounded-lg">
      <p className="text-lg mb-2 text-gris-claro flex items-center justify-center">
        <Users className="mr-2" /> Total Clientes
      </p>
      <p className="text-3xl font-bold text-amarillo-dorado">
        {resumenDelMes.totalClientes}
      </p>
    </div>
    
    <div className="text-center bg-azul-oscuro bg-opacity-30 p-4 rounded-lg">
      <p className="text-lg mb-2 text-gris-claro flex items-center justify-center">
        <Anchor className="mr-2" /> Total Embarcaciones
      </p>
      <p className="text-3xl font-bold text-verde-esmeralda">
        {resumenDelMes.totalEmbarcaciones}
      </p>
    </div>
    
    <div className="text-center bg-azul-oscuro bg-opacity-30 p-4 rounded-lg">
      <p className="text-lg mb-2 text-gris-claro flex items-center justify-center">
        <Calendar className="mr-2" /> Total Movimientos
      </p>
      <p className="text-3xl font-bold text-celeste-claro">
        {resumenDelMes.totalMovimientos}
      </p>
    </div>
  </div>
  <div className="flex justify-center mt-4">
  <Button 
    onClick={descargarResumenMes}
    className="bg-amarillo-dorado text-black hover:bg-yellow-400 transition"
  >
    <Download className="h-5 w-5 mr-2" /> Exportar Resumen a Excel
  </Button>
</div>
</Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-amarillo-dorado flex items-center">
              <Calendar className="mr-2" />
              Movimientos {selectedView === 'mes' ? 'del Mes' : 'del Año'}
            </CardTitle>

            <div className="flex items-center space-x-2">
  <DatePicker
    selected={selectedDate}
    onChange={(date) => setSelectedDate(date)}
    dateFormat="MMMM yyyy"
    showMonthYearPicker
    locale="es"
    className="bg-azul-claro text-black px-2 py-1 rounded"
  />
</div>
          </CardHeader>

          <CardContent>
            <div className="overflow-x-auto">
              {dates.length > 0 && (
                <div className="sticky top-2 z-10 mb-6 rounded-lg border-2 border-amarillo-dorado/70 bg-azul-oscuro/90 p-4 shadow-lg">
                  <p className="mb-2 text-sm font-semibold text-amarillo-dorado">
                    Selección de fechas para descarga de Excel
                  </p>
                  <p className="mb-3 text-xs text-gris-claro">Tocá una o varias fechas para incluirlas en el Excel consolidado con toda la información.</p>

                  <div className="mb-3 flex flex-wrap gap-2">
                    {dates.map((date) => {
                      const isSelected = selectedExportDates.includes(date);

                      return (
                        <Button
                          key={`selector-${date}`}
                          onClick={() => toggleDateForExport(date)}
                          className={`text-sm ${
                            isSelected
                              ? 'bg-amarillo-dorado text-black hover:bg-yellow-400'
                              : 'bg-azul-claro text-black hover:bg-celeste-claro'
                          }`}
                        >
                          {isSelected ? '✓ ' : ''}
                          {format(parseISO(date), 'dd/MM/yyyy', { locale: esLocale })}
                        </Button>
                      );
                    })}
                  </div>

                  <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                    <p className="text-xs text-blanco-puro">
                      Fechas seleccionadas: <span className="font-bold text-amarillo-dorado">{selectedExportDates.length}</span>
                    </p>
                    <div className="flex gap-2">
                      <Button onClick={() => setSelectedExportDates(dates)} className="bg-azul-claro text-black hover:bg-celeste-claro">
                        Seleccionar todas
                      </Button>
                      <Button onClick={() => setSelectedExportDates([])} className="bg-azul-claro text-black hover:bg-celeste-claro">
                        Limpiar selección
                      </Button>
                      <Button
                        onClick={handleDownloadSelectedDates}
                        disabled={selectedExportDates.length === 0}
                        className="bg-amarillo-dorado text-black hover:bg-yellow-400 transition disabled:opacity-60"
                      >
                        <Download className="h-4 w-4 mr-2" /> Descargar selección ({selectedExportDates.length})
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {dates.length > 0 ? (
                dates.map((date) => (
                  <div key={date} className="mb-10">
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 gap-2">
                      <h2 className="text-2xl font-bold text-amarillo-dorado">
                    {format(parseISO(date), 'dd MMMM yyyy', { locale: esLocale })}
                      </h2>
                      <div className="flex flex-wrap gap-2 items-center">
                        <Button
                          onClick={() => toggleDateForExport(date)}
                          className={`text-sm ${
                            selectedExportDates.includes(date)
                              ? 'bg-amarillo-dorado text-black hover:bg-yellow-400'
                              : 'bg-azul-claro text-black hover:bg-celeste-claro'
                          }`}
                        >
                          {selectedExportDates.includes(date) ? '✓ Quitar del Excel' : '+ Agregar al Excel'}
                        </Button>
                        <Button
                          onClick={() => toggleShowMovimientos(date)}
                          className="bg-verde-esmeralda text-blanco-puro"
                        >
                          {showMovimientos[date] ? (
                            <>
                              <EyeOff className="h-4 w-4 mr-1" /> Ocultar
                            </>
                          ) : (
                            <>
                              <Eye className="h-4 w-4 mr-1" /> Mostrar
                            </>
                          )}
                        </Button>
                        <Button
                          onClick={() => handleDownload(date)}
                          className="bg-verde-esmeralda text-blanco-puro"
                        >
                          <Download className="h-4 w-4 mr-1" /> Descargar Excel
                        </Button>
                      </div>
                    </div>

                    {showMovimientos[date] && (
                      <Table className="border-collapse border border-gray-600">
                        <TableHeader className="bg-azul-claro">
                          <TableRow>
                            <TableHead className="border border-gray-600">Cliente ID</TableHead>
                            <TableHead className="border border-gray-600">Matrícula</TableHead>
                            <TableHead className="border border-gray-600">Seguro</TableHead>
                            <TableHead className="border border-gray-600">
                              Vencimiento Seguro
                            </TableHead>
                            <TableHead className="border border-gray-600">
                              Vencimiento Matrícula
                            </TableHead>
                            <TableHead className="border border-gray-600">
                              Tipo de Embarcación
                            </TableHead>
                            <TableHead className="border border-gray-600">Nombre Fantasía</TableHead>
                            <TableHead className="border border-gray-600">HP</TableHead>
                            <TableHead className="border border-gray-600">Capacidad</TableHead>
                            <TableHead className="border border-gray-600">Color Casco</TableHead>
                            <TableHead className="border border-gray-600">Cliente</TableHead>
                            <TableHead className="border border-gray-600">DNI</TableHead>
                            <TableHead className="border border-gray-600">Teléfono 1</TableHead>
                            <TableHead className="border border-gray-600">Teléfono 2</TableHead>
                            <TableHead className="border border-gray-600">
                              Vencimiento Licencia
                            </TableHead>
                            <TableHead className="border border-gray-600">
                              Marca Vehículo
                            </TableHead>
                            <TableHead className="border border-gray-600">
                              Patente Vehículo
                            </TableHead>
                            <TableHead className="border border-gray-600">
                              Color Vehículo
                            </TableHead>
                            <TableHead className="border border-gray-600">
                              Fecha Ingreso
                            </TableHead>
                            <TableHead className="border border-gray-600">
                              Fecha Salida
                            </TableHead>
                            <TableHead className="border border-gray-600">
                              Tipo de Movimiento
                            </TableHead>
                            <TableHead className="border border-gray-600">Estado</TableHead>
                            <TableHead className="border border-gray-600">
                              Observaciones
                            </TableHead>
                          </TableRow>
                        </TableHeader>

                        <TableBody>
                          {groupedMovimientos[date].map((movimiento) => (
                            <TableRow
                              key={movimiento.id}
                              className="hover:bg-azul-claro/50"
                            >
                              <TableCell className="border border-gray-600">
                                {movimiento.cliente_id || 'N/A'}
                              </TableCell>
                              <TableCell className="border border-gray-600">
                                {movimiento.embarcacion_numeroMatricula || 'N/A'}
                              </TableCell>
                              <TableCell className="border border-gray-600">
                                {movimiento.embarcacion_seguro || 'N/A'}
                              </TableCell>
                              <TableCell className="border border-gray-600">
                                {movimiento.embarcacion_vencimientoSeguro
                                  ? format(
                                      parseISO(movimiento.embarcacion_vencimientoSeguro),
                                      'dd/MM/yyyy'
                                    )
                                  : 'N/A'}
                              </TableCell>
                              <TableCell className="border border-gray-600">
                                {movimiento.embarcacion_vencimientoMatricula
                                  ? format(
                                      parseISO(movimiento.embarcacion_vencimientoMatricula),
                                      'dd/MM/yyyy'
                                    )
                                  : 'N/A'}
                              </TableCell>
                              <TableCell className="border border-gray-600">
                                {movimiento.embarcacion_tipoEmbarcacion || 'N/A'}
                              </TableCell>
                              <TableCell className="border border-gray-600">
                                {movimiento.embarcacion_nombreFantasia || 'N/A'}
                              </TableCell>
                              <TableCell className="border border-gray-600">
                                {movimiento.embarcacion_hp || 'N/A'}
                              </TableCell>
                              <TableCell className="border border-gray-600">
                                {movimiento.embarcacion_capacidad || 'N/A'}
                              </TableCell>
                              <TableCell className="border border-gray-600">
                                {movimiento.embarcacion_colorCasco || 'N/A'}
                              </TableCell>
                              <TableCell className="border border-gray-600">
                                {`${movimiento.cliente_nombre || ''} ${
                                  movimiento.cliente_apellido || ''
                                }`.trim() || 'N/A'}
                              </TableCell>
                              <TableCell className="border border-gray-600">
                                {movimiento.cliente_dni || 'N/A'}
                              </TableCell>
                              <TableCell className="border border-gray-600">
                                {movimiento.cliente_telefono1 || 'N/A'}
                              </TableCell>
                              <TableCell className="border border-gray-600">
                                {movimiento.cliente_telefono2 || 'N/A'}
                              </TableCell>
                              <TableCell className="border border-gray-600">
                                {movimiento.cliente_vencimientoLicencia
                                  ? format(
                                      parseISO(movimiento.cliente_vencimientoLicencia),
                                      'dd/MM/yyyy'
                                    )
                                  : 'N/A'}
                              </TableCell>
                              <TableCell className="border border-gray-600">
                                {movimiento.vehiculo_marca || 'N/A'}
                              </TableCell>
                              <TableCell className="border border-gray-600">
                                {movimiento.vehiculo_patente || 'N/A'}
                              </TableCell>
                              <TableCell className="border border-gray-600">
                                {movimiento.vehiculo_color || 'N/A'}
                              </TableCell>
                              <TableCell className="border border-gray-600">
                                {movimiento.fechaHoraIngreso
                                  ? format(
                                      parseISO(movimiento.fechaHoraIngreso),
                                      'dd/MM/yyyy HH:mm'
                                    )
                                  : 'N/A'}
                              </TableCell>
                              <TableCell className="border border-gray-600">
                                {movimiento.fechaHoraSalida
                                  ? format(
                                      parseISO(movimiento.fechaHoraSalida),
                                      'dd/MM/yyyy HH:mm'
                                    )
                                  : 'N/A'}
                              </TableCell>
                              <TableCell className="border border-gray-600">
                                {movimiento.tipoMovimiento || 'N/A'}
                              </TableCell>
                              <TableCell className="border border-gray-600">
                                {movimiento.estado || 'N/A'}
                              </TableCell>
                              <TableCell className="border border-gray-600">
                                {movimiento.observaciones || 'N/A'}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </div>
                ))
              ) : (
                <p className="text-center py-8 text-xl">
                  No hay movimientos registrados para este período
                </p>
              )}

              {dates.length > 0 && (
                <div className="sticky bottom-4 mt-6 flex justify-end">
                  <Button
                    onClick={handleDownloadSelectedDates}
                    disabled={selectedExportDates.length === 0}
                    className="bg-amarillo-dorado text-black hover:bg-yellow-400 transition disabled:opacity-60"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Descargar selección ({selectedExportDates.length})
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
       
      </main>
    </div>
  );
};

export default HistoricoMovimientos;
